import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, Car, ArrowRight, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { paymentAPI, tipAPI } from '../lib/api';
import { devError } from '../lib/devLog';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const tipId = searchParams.get('tip_id');
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [isTip, setIsTip] = useState(false);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      if (tipId) {
        setIsTip(true);
        pollTipStatus();
      } else {
        pollPaymentStatus();
      }
    }
  }, [sessionId]);

  const pollTipStatus = async () => {
    if (attempts >= 5) { setStatus('timeout'); return; }
    try {
      const response = await tipAPI.getStatus(sessionId);
      setPaymentData(response.data);
      if (response.data.status === 'paid') {
        setStatus('success');
      } else {
        setAttempts(prev => prev + 1);
        setTimeout(pollTipStatus, 2000);
      }
    } catch (error) {
      devError('Error checking tip status:', error);
      setStatus('error');
    }
  };

  const pollPaymentStatus = async () => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await paymentAPI.getStatus(sessionId);
      setPaymentData(response.data);

      if (response.data.payment_status === 'paid' || response.data.status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      devError('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl border border-zinc-200 p-8 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {isTip ? 'Processing Tip' : 'Verifying Payment'}
            </h1>
            <p className="text-zinc-600">Please wait while we confirm your {isTip ? 'tip' : 'payment'}...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className={`w-16 h-16 ${isTip ? 'bg-pink-100' : 'bg-green-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
              {isTip ? <Heart className="w-8 h-8 text-pink-500 fill-pink-500" /> : <CheckCircle className="w-8 h-8 text-green-600" />}
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {isTip ? 'Tip Sent!' : 'Payment Successful!'}
            </h1>
            <p className="text-zinc-600 mb-6">
              {isTip
                ? 'Thank you for your generosity! Your tip has been sent to the driver.'
                : 'Thank you for your payment. Your job has been confirmed and a driver will be assigned soon.'}
            </p>
            {paymentData && (
              <div className={`${isTip ? 'bg-pink-50' : 'bg-zinc-50'} rounded-lg p-4 mb-6`}>
                <p className="text-sm text-zinc-500">{isTip ? 'Tip Amount' : 'Amount Paid'}</p>
                <p className={`text-2xl font-bold ${isTip ? 'text-pink-600' : 'text-green-600'}`}>${paymentData.amount?.toFixed(2)}</p>
              </div>
            )}
            <Link to="/dashboard">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8" data-testid="go-to-dashboard-btn">
                Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-yellow-600" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Verification Timeout
            </h1>
            <p className="text-zinc-600 mb-6">
              Payment verification is taking longer than expected. Please check your dashboard for the status.
            </p>
            <Link to="/dashboard">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8">
                Go to Dashboard
              </Button>
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Car className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Something Went Wrong
            </h1>
            <p className="text-zinc-600 mb-6">
              We couldn't verify your payment. Please check your dashboard or try again.
            </p>
            <Link to="/dashboard">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8">
                Go to Dashboard
              </Button>
            </Link>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Car className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Session Expired
            </h1>
            <p className="text-zinc-600 mb-6">
              Your payment session has expired. Please try again from your dashboard.
            </p>
            <Link to="/dashboard">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8">
                Go to Dashboard
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
